/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { fetchGraphQl, createGuestCart } from '@/cart/api';
import { state } from '@/cart/lib/state';
import { CartModel } from '@/cart/data/models';
import { transformCart } from '@/cart/data/transforms';
import { events } from '@adobe-commerce/event-bus';
import { handleFetchError } from '@/cart/lib/fetch-error';
import { getPersistedCartData } from '@/cart/lib/persisted-data';

import { ADD_PRODUCTS_TO_CART_MUTATION } from './graphql/AddProductsToCartMutation';
import { publishCartUpdateEvents, publishOpenCartEvent } from '@/cart/lib/acdl';

export const addProductsToCart = async (
  items: {
    sku: string;
    parentSku?: string;
    quantity: number;
    optionsUIDs?: string[];
    enteredOptions?: { uid: string; value: string }[];
    customFields?: Record<string, any>;
  }[]
): Promise<CartModel | null> => {
  const cartId =
    state.cartId ||
    (await createGuestCart().then((id) => {
      return id;
    }));

  return fetchGraphQl(ADD_PRODUCTS_TO_CART_MUTATION, {
    variables: {
      cartId,
      cartItems: items.map(
        ({
          sku,
          parentSku: parent_sku,
          quantity,
          optionsUIDs: selected_options,
          enteredOptions: entered_options,
          customFields,
        }) => ({
          sku,
          parent_sku,
          quantity,
          selected_options,
          entered_options,
          ...(customFields ? customFields : {}),
        })
      ),
    },
  }).then(({ errors, data }) => {
    // handle errors
    const _errors = [
      ...(data?.addProductsToCart?.user_errors ?? []),
      ...(errors ?? []),
    ];

    if (_errors.length > 0) return handleFetchError(_errors);

    // transform cart
    const payload = transformCart(data.addProductsToCart.cart);

    // Get previous cart data before emitting events
    const previousCart = getPersistedCartData();
    const previousItems = previousCart?.items || [];

    // emit events
    events.emit('cart/updated', payload);
    events.emit('cart/data', payload);

    // emit add to cart event with affected items
    if (payload) {
      // Separate new items and updated items
      const newItems = payload.items.filter((item) => {
        return !previousItems.some((prev) => prev.sku === item.sku);
      });

      const updatedItems = payload.items.filter((item) => {
        const previousItem = previousItems.find(
          (prev) => prev.sku === item.sku
        );
        // Emit update event if quantity has changed
        return previousItem && item.quantity !== previousItem.quantity;
      });

      // Emit add event for new items
      if (newItems.length > 0) {
        events.emit('cart/product/added', newItems);
      }

      // Emit update event for existing items
      if (updatedItems.length > 0) {
        events.emit('cart/product/updated', updatedItems);
      }
    }

    // data collection
    if (payload) {
      const updatedItems = payload.items.filter((item) =>
        items.some(({ sku }) => sku === item.topLevelSku)
      );
      
      // Check if cart was empty before adding items
      const wasCartEmpty = !previousCart || (previousCart.totalQuantity ?? 0) === 0;
      const hasItemsNow = (payload.totalQuantity ?? 0) > 0;
      
      if (wasCartEmpty && hasItemsNow) {
        // Publish open cart event when cart goes from empty to having items
        publishOpenCartEvent(payload, updatedItems, state.locale ?? 'en-US');
      } else {
        // Otherwise publish cart update events
        publishCartUpdateEvents(payload, updatedItems, state.locale ?? 'en-US');
      }
    }

    return payload;
  });
};
